import { Stack, Typography, Box, Divider, Button, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatRelative } from 'date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';

type Props = { pipeline: any; onClose?: () => void };

const PipelineHeader = ({ pipeline, onClose }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Stack
      direction={'row'}
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      className="glass-card-light"
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: '12px',
        borderBottom: 'none',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        bgcolor: 'rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <AccountTreeIcon color="secondary" sx={{ opacity: 0.8 }} />
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1rem', lineHeight: 1.2 }}
          >
            {pipeline ? pipeline.name : 'Pipeline Builder'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}
          >
            {pipeline ? 'Ingestion Pipeline' : 'Create New Pipeline'}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        {pipeline && (
          <Stack
            direction="row"
            spacing={3}
            divider={<Divider orientation="vertical" flexItem sx={{ height: 16, my: 'auto' }} />}
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            {pipeline.createdDate && (
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {t('common.created')}: {formatRelative(new Date(pipeline.createdDate), new Date())}
                </Typography>
                <PersonOutlineIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {pipeline.createdBy}
                </Typography>
              </Stack>
            )}
            {pipeline.lastUpdatedDate && (
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {t('common.updated')}: {formatRelative(new Date(pipeline.lastUpdatedDate), new Date())}
                </Typography>
                {pipeline.lastUpdatedBy && (
                  <>
                    <PersonOutlineIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      {pipeline.lastUpdatedBy}
                    </Typography>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {onClose && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={onClose}
            startIcon={<CloseIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap' }}
          >
            {t('common.buttons.close')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default PipelineHeader;
